import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import CustomAvatar from '../../../components/elements/CustomAvatar';
import { getDateAndTime } from '../../../lib/utils';
import {
  buildSummary,
  getRelativeLabel,
  getUserName,
  processEntries,
} from './historyUtils';

const DiffText = ({ oldVal, newVal }) => {
  if (!oldVal && newVal) {
    return (
      <Typography component="span" sx={{ color: 'success.dark' }}>
        {newVal}
      </Typography>
    );
  }

  if (oldVal && !newVal) {
    return (
      <Typography component="span" sx={{ color: 'error.dark' }}>
        removed ({oldVal})
      </Typography>
    );
  }

  if (oldVal && newVal) {
    return (
      <>
        <Typography component="span" sx={{ color: 'text.secondary' }}>
          {oldVal}
        </Typography>
        {' → '}
        <Typography component="span" sx={{ color: 'success.dark' }}>
          {newVal}
        </Typography>
      </>
    );
  }

  return null;
};

const Timestamp = ({ iso }) => {
  const relative = getRelativeLabel(iso);

  if (!relative) return getDateAndTime(iso);

  const dateObj = new Date(iso);
  const tooltipDate = `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
  const tooltipTime = dateObj.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return (
    <Tooltip title={`${tooltipDate} ${tooltipTime}`} arrow>
      <span>{relative}</span>
    </Tooltip>
  );
};

/* ================= COMPONENT ================= */

const HistoryFeed = ({ entries = [], isLoading }) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [order, setOrder] = useState('desc');

  const toggle = useCallback((id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const data = useMemo(() => {
    let processed = processEntries(entries);

    const q = search.toLowerCase();

    if (q) {
      processed = processed.filter((e) => {
        const user = getUserName(e.performed_by_user).toLowerCase();
        const action = (e.action_type || '').toLowerCase();
        const summary = buildSummary(e).toLowerCase();

        const changesText = (e.changes || [])
          .flatMap((c) => [c.field, c.old, c.new])
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return (
          user.includes(q) ||
          action.includes(q) ||
          summary.includes(q) ||
          changesText.includes(q)
        );
      });
    }

    processed.sort((a, b) =>
      order === 'desc'
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at),
    );

    return processed;
  }, [entries, search, order]);

  if (isLoading) {
    return (
      <Box p={2}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={48} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box p={2}>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search activity..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <ToggleButtonGroup
            size="small"
            value={order}
            exclusive
            onChange={(e, val) => val && setOrder(val)}>
            <ToggleButton value="desc">Newest</ToggleButton>
            <ToggleButton value="asc">Oldest</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {data.length === 0 ? (
        <Box p={4} textAlign="center">
          <Typography color="text.secondary">No activity yet</Typography>
        </Box>
      ) : (
        data.map((entry) => {
          const isCreate = entry.action_type === 'CREATE';
          const isSingle = entry.changes.length === 1;
          const isExpandable = !isCreate && entry.changes.length > 1;
          const isOpen = isSingle || expanded[entry.id];

          return (
            <Box
              key={entry.id}
              px={2}
              py={1.5}
              borderBottom="1px solid"
              borderColor="divider">
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <CustomAvatar
                  firstName={entry.performed_by_user.first_name}
                  lastName={entry.performed_by_user.last_name}
                  size={22}
                />

                <Box flex={1}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Typography variant="body2" sx={{ lineHeight: '20px' }}>
                      <strong>{getUserName(entry.performed_by_user)}</strong>{' '}
                      {buildSummary(entry)}
                    </Typography>

                    {isExpandable && (
                      <IconButton
                        size="small"
                        onClick={() => toggle(entry.id)}
                        sx={{ padding: '1px', marginTop: '-1px' }}>
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 16,
                            transform: isOpen
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </IconButton>
                    )}
                  </Stack>

                  {isCreate ? null : isSingle ? (
                    entry.changes.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {entry.changes[0].field}:{' '}
                        <DiffText
                          oldVal={entry.changes[0].old}
                          newVal={entry.changes[0].new}
                        />
                      </Typography>
                    )
                  ) : (
                    <Collapse in={isOpen}>
                      <Box>
                        {entry.changes.map((c, i) => (
                          <Box
                            key={i}
                            display="grid"
                            gridTemplateColumns="160px 1fr"
                            gap={1}
                            py={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              {c.field}
                            </Typography>

                            <Typography variant="body2">
                              <DiffText oldVal={c.old} newVal={c.new} />
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  <Timestamp iso={entry.created_at} />
                </Typography>
              </Stack>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default HistoryFeed;